/**
 * WORKER EVENT LISTENERS
 *
 * React hook that subscribes to Tauri events from the local worker process.
 * Updates workerStore and worldStore in response to worker status, progress,
 * job completion, and log events.
 *
 * No-op on web (non-Tauri) builds.
 */

import { useEffect } from "react";
import { isTauri } from "@/hooks/usePlatform";
import { useWorkerStore } from "@/stores/workerStore";
import { useWorldStore } from "@/stores/worldStore";
import {
  useReviewStore,
  type ActivityType,
  type ActivityMeta,
  type ChatMessage,
  type AIModel,
} from "@/stores/reviewStore";
import { useWorkflowStore } from "@/stores/workflowStore";
import { workflowModelToAIModel } from "@/features/review/AIConversation";
import type {
  WorkerState,
  WorkerLog,
  WorkerProgressEvent,
  DepsStatus,
} from "@/types/worker";

interface WorkerStatusEvent {
  state: WorkerState;
  pid?: number;
}

interface JobCompletedEvent {
  jobId: string;
  taskId: string;
  result?: Record<string, any>;
}

interface JobRunningEvent {
  jobId: string;
  taskId: string;
  status?: string;
}

interface JobFailedEvent {
  jobId: string;
  taskId: string;
  error: string;
  status?: string;
}

const MAX_ACTIVITY_TEXT = 1800;
const MAX_ACTIVITY_BLOCK = 6000;

// ── Event buffer for early events before run/task exists ─────────────
const MAX_BUFFER_PER_TASK = 100;
const _eventBuffer = new Map<string, ChatMessage[]>();

export function flushBufferedEvents() {
  if (_eventBuffer.size === 0) return;
  const store = useReviewStore.getState();
  const world = useWorldStore.getState();
  const flushed: string[] = [];

  _eventBuffer.forEach((messages, taskId) => {
    // Only flush if the task now exists in worldStore
    const exists = world.tasks.some((t) => t.id === taskId);
    if (!exists) return;

    store.ensureRunThread(taskId);
    for (const msg of messages) {
      store.appendActivity(taskId, msg);
    }
    flushed.push(taskId);
  });

  for (const id of flushed) {
    _eventBuffer.delete(id);
  }
}

function clipText(value: string, max: number): string {
  if (value.length <= max) return value;
  return `${value.slice(0, max - 1)}…`;
}

function readString(payload: Record<string, unknown>, ...keys: string[]): string | undefined {
  for (const key of keys) {
    const value = payload[key];
    if (typeof value === "string" && value.trim().length > 0) {
      return value;
    }
  }
  return undefined;
}

function readObject(payload: Record<string, unknown>, key: string): Record<string, unknown> | undefined {
  const value = payload[key];
  if (value && typeof value === "object" && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }
  return undefined;
}

function readOutput(payload: Record<string, unknown>): string | undefined {
  const raw = payload.output;
  if (typeof raw === "string" && raw.trim().length > 0) {
    return raw.trim();
  }
  if (raw && typeof raw === "object") {
    try {
      return JSON.stringify(raw, null, 2);
    } catch {
      return String(raw);
    }
  }
  return undefined;
}

function normalizeLogMessage(message: string): string {
  const withoutPrefix = message.replace(
    /^\d{4}-\d{2}-\d{2}\s+\d{2}:\d{2}:\d{2},\d{3}\s+\[[A-Z]+\]\s+[^:]+:\s*/,
    "",
  );
  return withoutPrefix.trim();
}

function inferCodeLanguage(nodeType?: string, text?: string): string {
  const sample = (text ?? "").slice(0, 500);
  if (nodeType === "test") return "bash";
  if (nodeType === "code" && /diff --git|@@\s+-\d+/.test(sample)) return "diff";
  if (/^\s*[{[]/.test(sample)) return "json";
  if (nodeType === "plan" || nodeType === "subtasks") return "markdown";
  return "text";
}

function formatReviewOutput(rawOutput: string): string {
  try {
    const parsed = JSON.parse(rawOutput);
    const parts: string[] = [];
    if (parsed.verdict) {
      parts.push(`**Verdict: ${parsed.verdict.toUpperCase()}**`);
    }
    if (parsed.summary) {
      parts.push(parsed.summary);
    }
    if (Array.isArray(parsed.feedback) && parsed.feedback.length > 0) {
      parts.push("**Feedback:**");
      for (const item of parsed.feedback) {
        if (typeof item === "string") {
          parts.push(`- ${item}`);
        } else if (item?.message) {
          const prefix = item.severity ? `[${item.severity}] ` : "";
          const file = item.file ? ` (${item.file})` : "";
          parts.push(`- ${prefix}${item.message}${file}`);
        }
      }
    }
    if (parts.length > 0) return parts.join("\n\n");
  } catch {
    // Not JSON, fall through
  }
  return rawOutput;
}

function resolveReviewModel(taskId: string): AIModel {
  return resolveNodeModel(taskId, "llm_review");
}

// Resolve the AI model for the currently active workflow node (for streaming output attribution)
function resolveActiveModel(taskId: string): AIModel {
  const task = useWorldStore.getState().tasks.find((t) => t.id === taskId);
  const currentNode = (task?.metadata as Record<string, unknown>)?.currentNode as string | undefined;
  if (!currentNode) return "claude-code";

  const workflowId = task?.workflowId;
  if (!workflowId) return "claude-code";

  const workflow = useWorkflowStore.getState().workflows.find((w) => w.id === workflowId);
  if (!workflow) return "claude-code";

  // Find the node matching currentNode (could be nodeId or nodeType)
  const node = workflow.nodes.find((n) => n.id === currentNode || n.type === currentNode);
  if (!node) return "claude-code";

  return workflowModelToAIModel(node.config.provider, node.config.model);
}

function resolveNodeModel(taskId: string, nodeType: string, eventProvider?: string): AIModel {
  // Prefer provider from backend progress event (authoritative)
  if (eventProvider) return workflowModelToAIModel(eventProvider);

  const task = useWorldStore.getState().tasks.find((t) => t.id === taskId);
  const workflowId = task?.workflowId;
  if (!workflowId) return "claude-code";

  const workflow = useWorkflowStore.getState().workflows.find((w) => w.id === workflowId);
  if (!workflow) return "claude-code";

  const node = workflow.nodes.find((n) => n.type === nodeType);
  if (!node) return "claude-code";

  return workflowModelToAIModel(node.config.provider, node.config.model);
}

// Detect lines that are AI assistant natural language (should be chat bubble, not output box).
// Conservative: only strong signals like sentences starting with pronouns, markdown, etc.
const AI_CHAT_LINE_PATTERNS = [
  /^(I'll |I'm |I |Let me |Let's |Here |Here's |The |This |That |Now |First |Next |Looking |Sure|OK|Okay|Great|Done|Perfect|Alright|Understood)/,
  /^(Based on|According to|It looks like|It seems|After |Before |To |In order to|We |You )/,
  /^#{1,3}\s+\S/,       // markdown headers
  /^\*\*[^*]+\*\*/,     // bold markdown
  /^- [A-Z]/,           // markdown list with sentence
];

function looksLikeAiChat(line: string): boolean {
  const trimmed = line.trim();
  if (!trimmed || trimmed.length < 8) return false;
  // Must not have CLI-style prefixes
  if (/^\[.+\]/.test(trimmed) || /^[$>#]/.test(trimmed) || /^\d+%/.test(trimmed)) return false;
  // Check for AI chat patterns
  if (AI_CHAT_LINE_PATTERNS.some(p => p.test(trimmed))) return true;
  // Long sentence (5+ words, starts with uppercase, has punctuation) — likely natural language
  const words = trimmed.split(/\s+/);
  if (words.length >= 6 && /^[A-Z]/.test(trimmed) && /[.!?:,]/.test(trimmed)) return true;
  return false;
}

function extractCommandSnippet(message: string): string | undefined {
  const match = message.match(/Command '\[(.+?)\]'/);
  if (!match?.[1]) return undefined;
  return clipText(`[${match[1]}]`, 1200);
}

function classifyLogActivity(log: WorkerLog): {
  activityType: ActivityType;
  title: string;
  details?: string;
  variant?: ActivityMeta["variant"];
  codeBlocks?: ChatMessage["codeBlocks"];
} | null {
  const raw = (log.message ?? "").trim();
  if (!raw) return null;
  const normalized = normalizeLogMessage(raw);
  if (!normalized) return null;

  const lower = normalized.toLowerCase();
  if (lower.startsWith("unknown notification:")) {
    return null;
  }

  if (lower === "local worker ready") {
    return {
      activityType: "info",
      title: "Local worker ready",
      variant: "status",
    };
  }

  if (normalized.startsWith("Traceback")) {
    return {
      activityType: "error",
      title: "Worker traceback",
      variant: "output",
      codeBlocks: [{ language: "text", code: clipText(normalized, MAX_ACTIVITY_BLOCK) }],
    };
  }

  if (/^\s*File "\S+"/.test(normalized) || /^[A-Za-z_][A-Za-z0-9_]*Error:/.test(normalized)) {
    return {
      activityType: "error",
      title: "Worker error detail",
      variant: "output",
      codeBlocks: [{ language: "text", code: clipText(normalized, MAX_ACTIVITY_BLOCK) }],
    };
  }

  if (lower.includes("command '['") && lower.includes("timed out")) {
    const commandSnippet = extractCommandSnippet(normalized);
    return {
      activityType: "error",
      title: "Command timed out",
      details: clipText(normalized, MAX_ACTIVITY_TEXT),
      variant: "command",
      codeBlocks: commandSnippet
        ? [{ language: "bash", code: commandSnippet }]
        : undefined,
    };
  }

  if (lower.includes("timed out")) {
    return {
      activityType: log.level === "error" ? "error" : "progress",
      title: clipText(normalized, MAX_ACTIVITY_TEXT),
      variant: "status",
    };
  }

  if (log.level === "error" || lower.includes("failed")) {
    return {
      activityType: "error",
      title: clipText(normalized, MAX_ACTIVITY_TEXT),
      variant: "status",
    };
  }

  if (log.level === "warn" || lower.includes("warning")) {
    return {
      activityType: "progress",
      title: clipText(normalized, MAX_ACTIVITY_TEXT),
      variant: "status",
    };
  }

  return {
    activityType: "info",
    title: clipText(normalized, MAX_ACTIVITY_TEXT),
    variant: "status",
  };
}

function patchTaskState(
  taskId: string,
  updater: (task: any) => Record<string, unknown> | null,
) {
  useWorldStore.setState((state) => {
    let changed = false;
    const tasks = state.tasks.map((task) => {
      if (task.id !== taskId) return task;
      const patch = updater(task);
      if (!patch) return task;
      changed = true;
      return { ...task, ...patch };
    });
    return changed ? { ...state, tasks } : state;
  });
}

function pushWorkerActivity(
  taskId: string,
  content: string,
  activityType: ActivityType,
  extra?: {
    nodeId?: string;
    nodeType?: string;
    nodeLabel?: string;
    title?: string;
    details?: string;
    source?: ActivityMeta["source"];
    variant?: ActivityMeta["variant"];
    codeBlocks?: ChatMessage["codeBlocks"];
  },
) {
  const message: ChatMessage = {
    id: `act-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    role: "system",
    content: extra?.title ?? content,
    codeBlocks: extra?.codeBlocks,
    timestamp: new Date().toISOString(),
    displayType: "activity",
    activityMeta: {
      activityType,
      nodeId: extra?.nodeId,
      nodeType: extra?.nodeType,
      nodeLabel: extra?.nodeLabel,
      title: extra?.title,
      details: extra?.details,
      source: extra?.source,
      variant: extra?.variant,
    },
  };

  const store = useReviewStore.getState();
  const run = store.runs.find((r) => r.id === taskId);
  const worldTask = useWorldStore.getState().tasks.find((t) => t.id === taskId);

  if (!run && !worldTask) {
    // Buffer for later — task may not exist yet
    const buf = _eventBuffer.get(taskId) ?? [];
    if (buf.length < MAX_BUFFER_PER_TASK) {
      buf.push(message);
      _eventBuffer.set(taskId, buf);
    }
    return;
  }

  store.ensureRunThread(taskId);
  store.appendActivity(taskId, message);
}

function resolveTaskIdForLog(lastActiveTaskId: string | null): string | null {
  const worker = useWorkerStore.getState();
  const activeJob = worker.activeJobId
    ? worker.jobs.find((job) => job.id === worker.activeJobId)
    : undefined;
  if (activeJob?.taskId) return activeJob.taskId;

  const runningJob = worker.jobs.find((job) => {
    const status = String(job.status ?? "").toLowerCase();
    return status === "running" || status === "processing" || status === "queued" || status === "pending";
  });
  if (runningJob?.taskId) return runningJob.taskId;

  const worldRunningTask = useWorldStore
    .getState()
    .tasks.find((task) => task.workflowStatus === "running" || task.workflowStatus === "queued");
  if (worldRunningTask?.id) return worldRunningTask.id;

  return lastActiveTaskId;
}

export function useWorkerEvents() {
  // Auto-start worker on mount (Tauri desktop only)
  useEffect(() => {
    if (!isTauri()) return;
    const timer = setTimeout(() => {
      const store = useWorkerStore.getState();
      if (store.workerState === "stopped") {
        store.start().catch(() => {
          // Non-fatal: user can start manually from worker panel
        });
      }
    }, 1500);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (!isTauri()) return;

    const unlisteners: (() => void)[] = [];
    let disposed = false;
    let lastActiveTaskId: string | null = null;

    (async () => {
      const { listen } = await import("@tauri-apps/api/event");

      // Worker status changes (started, stopped, error)
      const u1 = await listen<WorkerStatusEvent>("worker:status-changed", (e) => {
        const store = useWorkerStore.getState();
        store.setWorkerState(e.payload.state);
        if (e.payload.pid !== undefined) {
          store.setPid(e.payload.pid ?? null);
        }
        if (e.payload.state === "stopped") {
          store.setActiveJobId(null);
        }
      });
      unlisteners.push(u1);

      // Worker progress (node-by-node workflow execution)
      const u2 = await listen<WorkerProgressEvent>("worker:progress", (e) => {
        const payload = (e.payload ?? {}) as unknown as Record<string, unknown>;
        const taskId = readString(payload, "taskId", "task_id");
        const nodeId = readString(payload, "nodeId", "node_id", "step");
        const nodeType = readString(payload, "nodeType", "node_type", "step");
        const status = readString(payload, "status") ?? "running";
        const label = readString(payload, "label");
        const progressMessage = readString(payload, "message");
        const output = readOutput(payload);
        const eventProvider = readString(payload, "provider");
        if (!taskId) return;
        lastActiveTaskId = taskId;

        // Update task pipeline stage in worldStore based on node type
        const stageMap: Record<string, string> = {
          start: "backlog",
          plan: "planning",
          subtasks: "planning",
          code: "in_progress",
          test: "in_progress",
          llm_review: "ai_review",
          human_review: "human_review",
          deploy: "in_progress",
          end: "done",
        };

        const newStage = nodeType ? stageMap[nodeType] : undefined;
        if (newStage && status === "running") {
          patchTaskState(taskId, (task) => ({
            pipelineStage: newStage,
            workflowStatus: "running",
            metadata: {
              ...(task.metadata ?? {}),
              currentNode: nodeId,
              currentNodeLabel: label,
              current_step: nodeId,
              workflowStatus: "running",
            },
          }));
        }

        // Bridge to reviewStore activity
        const displayLabel = label || nodeType || "Processing";
        const normalizedStatus = status.toLowerCase();
        let title = displayLabel;
        if (normalizedStatus === "running" || normalizedStatus === "processing" || normalizedStatus === "pending") {
          title = `Running ${displayLabel}`;
        } else if (normalizedStatus === "completed" || normalizedStatus === "success") {
          title = `${displayLabel} completed`;
        } else if (normalizedStatus === "failed" || normalizedStatus === "error") {
          title = `${displayLabel} failed`;
        }
        const actType: ActivityType =
          normalizedStatus === "completed" || normalizedStatus === "success" ? "success"
          : normalizedStatus === "failed" || normalizedStatus === "error" ? "error"
          : "progress";
        // Route completed AI node output as chat bubbles with the correct model identity.
        // This covers: llm_review (formatted), plan, code, subtasks.
        const AI_CHAT_NODE_TYPES = new Set(["llm_review", "plan", "code", "subtasks"]);
        const isAiNodeCompleted =
          nodeType &&
          AI_CHAT_NODE_TYPES.has(nodeType) &&
          (normalizedStatus === "completed" || normalizedStatus === "success") &&
          output?.trim();

        if (isAiNodeCompleted) {
          const nodeModel = resolveNodeModel(taskId, nodeType!, eventProvider);
          const formattedContent = nodeType === "llm_review"
            ? formatReviewOutput(output!)
            : output!;
          const bubbleMessage: ChatMessage = {
            id: `${nodeType}-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
            role: "assistant",
            model: nodeModel,
            content: formattedContent,
            timestamp: new Date().toISOString(),
            displayType: "bubble",
          };

          const store = useReviewStore.getState();
          store.ensureRunThread(taskId);
          store.appendActivity(taskId, bubbleMessage);
          return;
        }

        const codeBlocks =
          output && output.trim().length > 0
            ? [{
                language: inferCodeLanguage(nodeType, output),
                code: clipText(output, MAX_ACTIVITY_BLOCK),
              }]
            : undefined;

        pushWorkerActivity(taskId, displayLabel, actType, {
          nodeId,
          nodeType,
          nodeLabel: label,
          title,
          details: progressMessage ? clipText(progressMessage, MAX_ACTIVITY_TEXT) : undefined,
          source: "worker-progress",
          variant: codeBlocks ? "output" : "status",
          codeBlocks,
        });
      });
      unlisteners.push(u2);

      // Worker log output
      const u3 = await listen<WorkerLog>("worker:log", (e) => {
        const log = e.payload;
        useWorkerStore.getState().addLog(log);

        const taskId = resolveTaskIdForLog(lastActiveTaskId);
        if (!taskId) return;

        const parsed = classifyLogActivity(log);
        if (!parsed) return;

        pushWorkerActivity(taskId, parsed.title, parsed.activityType, {
          title: parsed.title,
          details: parsed.details,
          source: "worker-log",
          variant: parsed.variant,
          codeBlocks: parsed.codeBlocks,
        });
      });
      unlisteners.push(u3);

      // Job transitioned to running
      const uRunning = await listen<JobRunningEvent>("worker:job-running", (e) => {
        const payload = (e.payload ?? {}) as unknown as Record<string, unknown>;
        const taskId = readString(payload, "taskId", "task_id");
        if (!taskId) return;
        lastActiveTaskId = taskId;

        const store = useWorkerStore.getState();
        const fallbackJobId = store.jobs.find(
          (job) => job.taskId === taskId && (job.status === "queued" || job.status === "running")
        )?.id;
        const jobId = readString(payload, "jobId", "job_id") ?? fallbackJobId;
        if (jobId) {
          store.updateJob(jobId, { status: "running", updatedAt: new Date().toISOString() });
          store.setActiveJobId(jobId);
        }

        patchTaskState(taskId, (task) => ({
          workflowStatus: "running",
          metadata: {
            ...(task.metadata ?? {}),
            workflowStatus: "running",
            currentNode: undefined,
            current_step: undefined,
            workflowLastOutput: "",
            workflowOutputTail: [],
          },
        }));

        pushWorkerActivity(taskId, "Agent started, analyzing codebase...", "info", {
          title: "Agent started",
          details: "Analyzing repository and preparing workflow steps.",
          source: "worker-progress",
          variant: "status",
        });
      });
      unlisteners.push(uRunning);

      // Job completed
      const u4 = await listen<JobCompletedEvent>("worker:job-completed", (e) => {
        const payload = (e.payload ?? {}) as unknown as Record<string, unknown>;
        const taskId = readString(payload, "taskId", "task_id");
        if (!taskId) return;
        lastActiveTaskId = taskId;

        const store = useWorkerStore.getState();
        const fallbackJobId = store.jobs.find(
          (job) => job.taskId === taskId && (job.status === "queued" || job.status === "running")
        )?.id;
        const jobId = readString(payload, "jobId", "job_id") ?? fallbackJobId;
        if (jobId) {
          store.updateJob(jobId, { status: "completed", updatedAt: new Date().toISOString() });
        }
        if (jobId && store.activeJobId === jobId) {
          store.setActiveJobId(null);
        }

        // Update task in worldStore
        const result = readObject(payload, "result");
        const resultStatus = readString(result ?? {}, "status");
        const workflowStatus =
          resultStatus === "failed" ? "failed" : "completed";
        patchTaskState(taskId, (task) => ({
          workflowStatus,
          metadata: {
            ...(task.metadata ?? {}),
            workflowStatus,
            currentNode: undefined,
          },
        }));
        void useWorldStore.getState().refreshTask(taskId);

        pushWorkerActivity(
          taskId,
          workflowStatus === "completed"
            ? "Workflow completed successfully"
            : "Workflow completed with errors",
          workflowStatus === "completed" ? "success" : "error",
          {
            title: workflowStatus === "completed" ? "Workflow completed" : "Workflow completed with errors",
            source: "worker-progress",
            variant: "status",
          },
        );
      });
      unlisteners.push(u4);

      // Job failed
      const u5 = await listen<JobFailedEvent>("worker:job-failed", (e) => {
        const payload = (e.payload ?? {}) as unknown as Record<string, unknown>;
        const taskId = readString(payload, "taskId", "task_id");
        if (!taskId) return;
        lastActiveTaskId = taskId;

        const store = useWorkerStore.getState();
        const fallbackJobId = store.jobs.find(
          (job) => job.taskId === taskId && (job.status === "queued" || job.status === "running")
        )?.id;
        const jobId = readString(payload, "jobId", "job_id") ?? fallbackJobId;
        const error = readString(payload, "error") ?? "Workflow failed";
        const status = (readString(payload, "status") ?? "failed").toLowerCase();
        const isCancelled = status === "cancelled" || error.toLowerCase() === "cancelled";

        if (jobId) {
          store.updateJob(jobId, {
            status: isCancelled ? "cancelled" : "failed",
            error,
            updatedAt: new Date().toISOString(),
          });
        }
        if (jobId && store.activeJobId === jobId) {
          store.setActiveJobId(null);
        }

        // Update task in worldStore
        patchTaskState(taskId, (task) => ({
          pipelineStage: isCancelled ? task.pipelineStage : "error",
          workflowStatus: isCancelled ? "cancelled" : "failed",
          metadata: {
            ...(task.metadata ?? {}),
            workflowStatus: isCancelled ? "cancelled" : "failed",
            workflowError: error,
            currentNode: undefined,
          },
        }));
        const workerExited = error.toLowerCase().includes("worker process exited unexpectedly");
        if (!workerExited) {
          void useWorldStore.getState().refreshTask(taskId);
        }

        pushWorkerActivity(
          taskId,
          isCancelled ? "Workflow cancelled" : `Workflow failed: ${error}`,
          isCancelled ? "info" : "error",
          {
            title: isCancelled ? "Workflow cancelled" : "Workflow failed",
            details: clipText(error, MAX_ACTIVITY_TEXT),
            source: "worker-progress",
            variant: "status",
          },
        );
      });
      unlisteners.push(u5);

      // Live output from CLI subprocess (line-by-line streaming)
      const uOutput = await listen<{ task_id: string; line: string }>("worker:output", (e) => {
        const payload = (e.payload ?? {}) as Record<string, unknown>;
        const taskId = readString(payload, "task_id", "taskId");
        const line = readString(payload, "line");
        if (!taskId || !line) return;
        lastActiveTaskId = taskId;

        patchTaskState(taskId, (task) => {
          const metadata = (task.metadata ?? {}) as Record<string, unknown>;
          const tailRaw = Array.isArray(metadata.workflowOutputTail) ? metadata.workflowOutputTail : [];
          const tail = tailRaw
            .filter((value): value is string => typeof value === "string" && value.trim().length > 0)
            .slice(-19);
          const nextLine = line.trim();
          const dedupedTail = tail[tail.length - 1] === nextLine ? tail : [...tail, nextLine];
          return {
            metadata: {
              ...metadata,
              workflowLastOutput: nextLine,
              workflowOutputTail: dedupedTail,
            },
          };
        });

        // Lines prefixed with [assistant] are AI chat — render as bubble, strip prefix
        if (line.startsWith("[assistant]")) {
          const chatText = line.slice("[assistant]".length).trim();
          if (chatText) {
            const bubbleMsg: ChatMessage = {
              id: `bubble-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
              role: "assistant",
              model: resolveActiveModel(taskId),
              content: chatText,
              timestamp: new Date().toISOString(),
              displayType: "bubble",
            };
            const store = useReviewStore.getState();
            store.ensureRunThread(taskId);
            store.appendActivity(taskId, bubbleMsg);
          }
        }
        // Result lines from AI subprocess — render as assistant bubble with markdown
        else if (/^\[result:(success|error|fail)\]/i.test(line)) {
          const chatText = line.replace(/^\[result:\w+\]\s*/i, "").trim();
          if (chatText) {
            const bubbleMsg: ChatMessage = {
              id: `result-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
              role: "assistant",
              model: resolveActiveModel(taskId),
              content: chatText,
              timestamp: new Date().toISOString(),
              displayType: "bubble",
            };
            const store = useReviewStore.getState();
            store.ensureRunThread(taskId);
            store.appendActivity(taskId, bubbleMsg);
          }
        }
        // Skip SSE noise lines (event markers, data prefixes, empty/whitespace)
        else if (/^\s*$/i.test(line) || /^(event|data|id|retry)\s*:?\s*$/i.test(line.trim()) || /^:\s*$/.test(line.trim())) {
          // no-op: bare SSE framing lines like "Event", "event:", "data:", ":"
        }
        else {
          // Strip [exec] prefix from status lines before displaying
          const cleanLine = line.replace(/^\[exec\]\s*/i, "");

          // Detect AI natural language — render as chat bubble instead of output box
          if (looksLikeAiChat(cleanLine)) {
            const bubbleMsg: ChatMessage = {
              id: `chat-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
              role: "assistant",
              model: resolveActiveModel(taskId),
              content: cleanLine,
              timestamp: new Date().toISOString(),
              displayType: "bubble",
            };
            const store = useReviewStore.getState();
            store.ensureRunThread(taskId);
            store.appendActivity(taskId, bubbleMsg);
          } else {
            // Attach active nodeId so output lines group with their node's progress messages
            const taskMeta = (useWorldStore.getState().tasks.find((t) => t.id === taskId)
              ?.metadata ?? {}) as Record<string, unknown>;
            const activeNodeId = taskMeta.currentNode as string | undefined;
            const activeNodeLabel = taskMeta.currentNodeLabel as string | undefined;
            pushWorkerActivity(taskId, cleanLine, "progress", {
              nodeId: activeNodeId,
              nodeLabel: activeNodeLabel,
              title: clipText(cleanLine, 200),
              source: "worker-progress",
              variant: "output",
            });
          }
        }
      });
      unlisteners.push(uOutput);

      // Deps check result (emitted after auto-check on worker start)
      const u6 = await listen<DepsStatus>("worker:deps-checked", (e) => {
        useWorkerStore.getState().setDepsStatus(e.payload);
      });
      unlisteners.push(u6);

      // If component unmounted while we were awaiting listeners, clean up immediately
      if (disposed) {
        unlisteners.forEach((fn) => fn());
        unlisteners.length = 0;
      }
    })().catch((err) => console.error("[useWorkerEvents] listener setup failed:", err));

    return () => {
      disposed = true;
      unlisteners.forEach((fn) => fn());
    };
  }, []);
}
